import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Monitor,
  Radio,
  PowerOff,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Tv2,
  Building2,
  Play,
  AlertTriangle,
  MapPin,
} from 'lucide-react';
import { Button, EmptyState, Skeleton } from '../ui';
import ScreenRow from './ScreenRow';
import TVDetails from '../TVDetails';
import TimelineEditor from '../../features/timelineEditor/TimelineEditor';
import { getNowPlayingForProject } from '../../api/contentApi';
import { deleteScreen } from '../../api/tvApi';
import { getDeviceDisplayName } from '../../utils/deviceLabel';
import { Trash2 } from 'lucide-react';
import { getActiveProjectId } from '../../constants/projectConstants';
import {
  UNCATEGORIZED,
  categorySortKey,
  formatCategoryLabel,
  getDeviceListId,
  getScreenCategoryFromDeviceId,
} from '../../utils/screenCategories';
import {
  computeKnownLocations,
  getGroupKey as getGroupKeyFn,
  compareByGroupThenName,
  computeCategoryCounts,
} from '../../utils/screenGrouping';
import './screens.css';

/**
 * ScreensView — Phase 2 of the professional redesign.
 *
 * Drop-in replacement for the legacy TVList component. Takes the same
 * props surface so Dashboard.jsx can swap imports with a one-line change.
 *
 * Features preserved from legacy TVList:
 *   - Listing all TV devices
 *   - Select-to-detail (renders TVDetails in a side panel)
 *   - Search across display name / id / location / resolution / playing title
 *   - Status filter (all / online / offline)
 *   - Stats row (total / online / offline / last seen < 5 min)
 *   - Pagination
 *   - Now-playing polling every 60s
 *   - Refresh action
 *
 * Features intentionally deferred (will be migrated in iteration):
 *   - Inline rename (Phase 2.5)
 *   - Category grouping + category filter (Phase 2.5)
 *   - Bulk profile apply (Phase 2.5)
 *   - Blast radius hardware-change warnings (Phase 2.5)
 *   - Schedules deeplink button per-row (Phase 2.5)
 *
 * All visual elements consume Phase 0 design tokens via CSS classes in
 * screens.css — no hardcoded colors, no inline styles for layout.
 */
const ITEMS_PER_PAGE = 12;

const ScreensView = ({
  tvs = [],
  selectedTV = null,
  onSelectTV,
  onRefreshTvs,
  onOpenSchedulesForDevice, // eslint-disable-line no-unused-vars
  blastRadiusByDeviceId = {},
}) => {
  // Memoize the normalized list so downstream hooks get a stable reference
  // when the parent passes a new array with the same contents (avoids
  // re-triggering dependent useMemos on every render).
  const tvList = useMemo(() => (Array.isArray(tvs) ? tvs : []), [tvs]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Inline timeline-editor expansion (Phase 2 timeline editor) ────
  // One row expanded at a time. Deep-linked via ?screen=<id> in the URL.
  const [expandedScreenId, setExpandedScreenId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('screen') || null;
    } catch {
      return null;
    }
  });
  const expandedRowRef = useRef(null);
  const didAutoScrollRef = useRef(false);

  const writeScreenToUrl = useCallback((id) => {
    try {
      const url = new URL(window.location.href);
      if (id) url.searchParams.set('screen', id);
      else url.searchParams.delete('screen');
      window.history.replaceState({}, '', url.toString());
    } catch {
      // URL API unavailable — ignore (older browsers).
    }
  }, []);

  const toggleExpanded = useCallback((device) => {
    const id = device?.deviceId || device?.tvId || null;
    if (!id) return;
    setExpandedScreenId((prev) => {
      const next = prev === id ? null : id;
      writeScreenToUrl(next);
      return next;
    });
  }, [writeScreenToUrl]);

  const collapseExpanded = useCallback(() => {
    setExpandedScreenId(null);
    writeScreenToUrl(null);
  }, [writeScreenToUrl]);

  // Sync back-forward navigation (browser back/forward buttons).
  useEffect(() => {
    const onPop = () => {
      try {
        const id = new URLSearchParams(window.location.search).get('screen') || null;
        setExpandedScreenId(id);
      } catch {
        // ignore
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ESC collapses the expanded panel (when the inner editor hasn't already
  // swallowed the key — inner editor handles its own-clip ESC first).
  useEffect(() => {
    if (!expandedScreenId) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      collapseExpanded();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedScreenId, collapseExpanded]);

  // ── Now playing polling ──────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState(null); // device object
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [nowPlayingMap, setNowPlayingMap] = useState({});
  const [nowPlayingServerTime, setNowPlayingServerTime] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getNowPlayingForProject(getActiveProjectId());
        if (cancelled) return;
        setNowPlayingMap(res?.nowPlaying || {});
        setNowPlayingServerTime(res?.serverTime || null);
      } catch (err) {
        if (!cancelled) console.warn('now-playing fetch failed:', err?.message);
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // ── Derived stats ────────────────────────────────────────────────
  // `recentlyActive` used to read Date.now() inside the useMemo body, which
  // makes the memo impure — react-hooks/purity flags it because the same
  // inputs produce different outputs as wall-clock time advances. We use a
  // tick state that bumps every minute so the "recently active" count stays
  // fresh without polluting the memo with a time side-effect.
  const [wallClockTick, setWallClockTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setWallClockTick(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  // ── Registered vs pending split ──────────────────────────────────
  const knownLocations = useMemo(() => computeKnownLocations(tvList), [tvList]);

  // A device is "set up" once an admin assigns a profile, names it,
  // AND assigns it to a known location — all collected in the setup
  // modal in one step. Devices with auto-seeded garbage locations
  // (hardware model names like "TX3", "H96_Max_M9") are NOT set up.
  const registeredDevices = useMemo(() => {
    if (!knownLocations.length) {
      // Fresh deployment with no known sites — fall back to profile + name
      return tvList.filter((d) => d.profileId && d.displayName);
    }
    return tvList.filter(
      (d) => d.profileId && d.displayName && knownLocations.includes(d.location)
    );
  }, [tvList, knownLocations]);
  const pendingDevices = useMemo(() => {
    const regIds = new Set(registeredDevices.map((d) => d.deviceId || d.tvId));
    return tvList.filter((d) => !regIds.has(d.deviceId || d.tvId));
  }, [tvList, registeredDevices]);

  // Stats are scoped to profiled devices only — the fleet you manage.
  const stats = useMemo(() => {
    const online = registeredDevices.filter((d) => d.status === 'online').length;
    const offline = registeredDevices.filter((d) => d.status !== 'online').length;
    const onlineIds = new Set(
      registeredDevices.filter((d) => d.status === 'online').map((d) => d.deviceId || d.tvId)
    );
    const playingNow = Object.keys(nowPlayingMap || {}).filter(
      (id) => nowPlayingMap[id]?.title && onlineIds.has(id)
    ).length;
    return {
      registered: registeredDevices.length,
      online,
      offline,
      pending: pendingDevices.length,
      playingNow,
    };
  }, [registeredDevices, pendingDevices, nowPlayingMap, wallClockTick]);

  const afterStatus = useMemo(() => {
    if (activeFilter === 'online') {
      return registeredDevices.filter((d) => d.status === 'online');
    }
    if (activeFilter === 'offline') {
      return registeredDevices.filter((d) => d.status === 'offline');
    }
    if (activeFilter === 'playing') {
      return registeredDevices.filter(
        (d) => d.status === 'online' && nowPlayingMap?.[d.deviceId || d.tvId]?.title
      );
    }
    if (activeFilter === 'pending') {
      return pendingDevices;
    }
    return registeredDevices;
  }, [registeredDevices, pendingDevices, activeFilter, nowPlayingMap]);

  // Available sites from the status-filtered set (for the dropdown)
  const siteOptions = useMemo(() => {
    const m = new Map();
    for (const d of afterStatus) {
      const cat = getScreenCategoryFromDeviceId(getDeviceListId(d));
      m.set(cat, (m.get(cat) || 0) + 1);
    }
    return Array.from(m.entries())
      .filter(([k]) => k !== UNCATEGORIZED)
      .sort(([a], [b]) => categorySortKey(a).localeCompare(categorySortKey(b)));
  }, [afterStatus]);

  // Reset site filter if it no longer exists in the available options
  useEffect(() => {
    if (siteFilter && !siteOptions.some(([k]) => k === siteFilter)) {
      setSiteFilter('');
    }
  }, [siteFilter, siteOptions]);

  const afterSite = useMemo(() => {
    if (!siteFilter) return afterStatus;
    return afterStatus.filter(
      (d) => getScreenCategoryFromDeviceId(getDeviceListId(d)) === siteFilter
    );
  }, [afterStatus, siteFilter]);

  const filtered = useMemo(() => {
    if (!searchQuery) return afterSite;
    const q = searchQuery.toLowerCase();
    return afterSite.filter((device) => {
      const id = device.deviceId || device.tvId || '';
      const npTitle = nowPlayingMap?.[id]?.title || '';
      const nr = device.nativeResolution;
      const resPieces = nr && nr.width && nr.height
        ? [
            `${nr.width}x${nr.height}`,
            `${nr.width}×${nr.height}`,
            nr.height >= 2160 ? '4k' : nr.height >= 1080 ? '1080p' : '',
          ].filter(Boolean)
        : [];
      const fields = [
        device.displayName,
        device.tvId,
        device.deviceId,
        device.location,
        device.model,
        npTitle,
        ...resPieces,
      ];
      return fields.some((f) => f && String(f).toLowerCase().includes(q));
    });
  }, [afterSite, searchQuery, nowPlayingMap]);

  // Reset to page 1 whenever filter inputs change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter, siteFilter]);

  // Scroll the deep-linked expanded row into view on first open. We guard
  // with a ref so reopening a row the user just scrolled to doesn't bounce.
  useEffect(() => {
    if (!expandedScreenId) {
      didAutoScrollRef.current = false;
      return;
    }
    if (didAutoScrollRef.current) return;
    const el = expandedRowRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    // Give the layout one frame to apply the open class (height animation).
    const raf = window.requestAnimationFrame(() => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {
        // ignore
      }
      didAutoScrollRef.current = true;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [expandedScreenId]);

  const getGroupKey = useCallback(
    (d) => getGroupKeyFn(d, knownLocations),
    [knownLocations],
  );

  const sortedFiltered = useMemo(() => {
    const copy = filtered.slice();
    copy.sort((a, b) => compareByGroupThenName(a, b, knownLocations));
    return copy;
  }, [filtered, knownLocations]);

  const categoryCounts = useMemo(
    () => computeCategoryCounts(sortedFiltered, knownLocations),
    [sortedFiltered, knownLocations],
  );

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / ITEMS_PER_PAGE));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedFiltered.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedFiltered, currentPage]);

  // ── Interleave section headers between device rows whenever the category
  // changes. Each header row is NOT counted against pagination — the page
  // size stays at ITEMS_PER_PAGE devices, and headers are just visual
  // dividers. Crosses-page continuation: if a site starts on the previous
  // page, the current page still re-emits its header so users always see
  // context for what they're looking at.
  const rowsWithHeaders = useMemo(() => {
    const rows = [];
    let prevGroup = null;
    for (const device of paginated) {
      const group = getGroupKey(device);
      if (group && group !== prevGroup) {
        rows.push({
          kind: 'section',
          label: group,
          count: categoryCounts.get(group) ?? 0,
        });
      }
      prevGroup = group;
      rows.push({ kind: 'device', device });
    }
    return rows;
  }, [paginated, categoryCounts]);

  const startItem = sortedFiltered.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, sortedFiltered.length);

  const isLoading = tvList.length === 0;
  const noResults = !isLoading && sortedFiltered.length === 0;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="screens">
      {/* ── Stats row — clickable filter cards ─────────────────── */}
      <div className="screens__stats">
        <StatTile
          tone="brand"
          active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
          icon={<Tv2 />}
          label="All screens"
          value={stats.registered}
          caption={<>Registered fleet</>}
        />
        <StatTile
          tone="good"
          active={activeFilter === 'online'}
          onClick={() => setActiveFilter(activeFilter === 'online' ? 'all' : 'online')}
          icon={<Radio />}
          label="Online"
          value={stats.online}
          caption={
            stats.registered > 0 ? (
              <>
                <span className="screens-stat__caption-strong">
                  {Math.round((stats.online / stats.registered) * 100)}%
                </span>{' '}
                of fleet
              </>
            ) : (
              <>No devices yet</>
            )
          }
        />
        <StatTile
          tone="danger"
          active={activeFilter === 'offline'}
          onClick={() => setActiveFilter(activeFilter === 'offline' ? 'all' : 'offline')}
          icon={<PowerOff />}
          label="Offline"
          value={stats.offline}
          caption={<>Not responding</>}
        />
        <StatTile
          tone="info"
          active={activeFilter === 'playing'}
          onClick={() => setActiveFilter(activeFilter === 'playing' ? 'all' : 'playing')}
          icon={<Play />}
          label="Playing"
          value={stats.playingNow}
          caption={<>Broadcasting content</>}
        />
      </div>

      {/* ── Pending devices banner ─────────────────────────────── */}
      {stats.pending > 0 && (
        <div className="screens__pending-banner">
          <div className="screens__pending-banner-text">
            <AlertTriangle size={16} />
            <span>
              <span className="screens__pending-banner-count">{stats.pending}</span>
              {' '}unregistered {stats.pending === 1 ? 'device' : 'devices'} waiting for setup
            </span>
          </div>
          <button
            type="button"
            className="screens__pending-banner-action"
            onClick={() => setActiveFilter('pending')}
          >
            Review {stats.pending === 1 ? 'device' : 'devices'}
          </button>
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="screens__toolbar">
        <div className="screens__toolbar-left">
          {siteOptions.length > 1 && (
            <div className="screens__site-filter">
              <span className="screens__site-filter-icon">
                <MapPin size={14} />
              </span>
              <select
                className="screens__site-filter-select"
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                aria-label="Filter by site"
              >
                <option value="">All sites ({siteOptions.length})</option>
                {siteOptions.map(([cat, count]) => (
                  <option key={cat} value={cat}>
                    {formatCategoryLabel(cat)} ({count})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="screens__search">
            <span className="screens__search-icon">
              <Search size={16} />
            </span>
            <input
              className="screens__search-input"
              type="text"
              placeholder="Search by name, location, content, or ID…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="screens__search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="screens__toolbar-right">
          {activeFilter !== 'all' && (
            <button
              type="button"
              className="screens__search-clear"
              onClick={() => setActiveFilter('all')}
              aria-label="Clear filter"
              style={{ marginRight: 4 }}
              title="Clear filter"
            >
              <X size={14} />
            </button>
          )}
          <span className="screens__result-count">
            <strong>{sortedFiltered.length}</strong>
            {sortedFiltered.length === 1 ? ' screen' : ' screens'}
            {activeFilter !== 'all' && ` (${activeFilter})`}
            {searchQuery && ' found'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Refresh"
            onClick={() => onRefreshTvs?.()}
          >
            <RefreshCw size={15} />
          </Button>
        </div>
      </div>

      {/* ── List + Detail split layout ──────────────────────────── */}
      <div
        className={[
          'screens__split',
          selectedTV && 'screens__split--with-detail',
        ].filter(Boolean).join(' ')}
      >
        <div className="screens__list">
          <div className="screens__list-header">
            <div />
            <div className="screens__list-header-cell">Screen</div>
            <div className="screens__list-header-cell">Now playing</div>
            <div className="screens__list-header-cell">Last seen</div>
            <div className="screens__list-header-cell">Status</div>
            <div />
          </div>

          {isLoading ? (
            <LoadingRows count={6} />
          ) : noResults ? (
            <div className="screens__list-empty">
              <EmptyState
                tone="brand"
                icon={<Monitor />}
                title={
                  searchQuery
                    ? `No screens match "${searchQuery}"`
                    : activeFilter === 'online'
                    ? 'No screens are online'
                    : activeFilter === 'offline'
                    ? 'No offline screens — everything is live'
                    : 'No screens yet'
                }
                description={
                  searchQuery
                    ? 'Try a different keyword, or clear the search to see all screens.'
                    : activeFilter !== 'all'
                    ? 'Switch to "All" to view every screen in the fleet.'
                    : 'Register your first TV device to start managing content across your properties.'
                }
                action={
                  searchQuery ? (
                    <Button variant="secondary" size="md" onClick={() => setSearchQuery('')}>
                      Clear search
                    </Button>
                  ) : null
                }
              />
            </div>
          ) : (
            rowsWithHeaders.map((row, idx) => {
              if (row.kind === 'section') {
                return (
                  <div
                    key={`section-${row.label}-${idx}`}
                    className="screens__section-header"
                    role="rowheader"
                  >
                    <span className="screens__section-header-icon" aria-hidden="true">
                      <Building2 size={14} />
                    </span>
                    <span className="screens__section-header-label">{row.label}</span>
                    <span className="screens__section-header-count">
                      {row.count} {row.count === 1 ? 'screen' : 'screens'}
                    </span>
                  </div>
                );
              }
              const device = row.device;
              const id = device.deviceId || device.tvId;
              const isExpanded = expandedScreenId === id;
              return (
                <React.Fragment key={id}>
                  <ScreenRow
                    device={device}
                    selected={
                      selectedTV &&
                      (selectedTV.deviceId === device.deviceId ||
                        selectedTV.tvId === device.tvId)
                    }
                    onClick={onSelectTV}
                    nowPlaying={nowPlayingMap?.[id]}
                    serverTime={nowPlayingServerTime}
                    timelineOpen={isExpanded}
                    onToggleTimeline={toggleExpanded}
                    onRequestDelete={(d) => { setDeleteError(null); setDeleteTarget(d); }}
                  />
                  <div
                    className={[
                      'screen-row__expansion',
                      isExpanded && 'screen-row__expansion--open',
                    ].filter(Boolean).join(' ')}
                    ref={isExpanded ? expandedRowRef : null}
                  >
                    <div className="screen-row__expansion-inner">
                      {isExpanded && (
                        <TimelineEditor
                          screenId={id}
                          projectId={getActiveProjectId()}
                          onClose={collapseExpanded}
                        />
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}

          {/* Pagination */}
          {!isLoading && !noResults && totalPages > 1 && (
            <div className="screens__footer">
              <span className="screens__page-info">
                Showing <strong>{startItem}</strong>–<strong>{endItem}</strong> of{' '}
                <strong>{sortedFiltered.length}</strong>
              </span>
              <div className="screens__page-nav">
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Previous page"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="screens__page-info" style={{ padding: '0 var(--space-2)' }}>
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Next page"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Detail panel (uses existing TVDetails component) ── */}
        {selectedTV && (
          <div className="screens__detail-panel">
            <TVDetails
              tv={selectedTV}
              onClose={() => onSelectTV?.(null)}
              onDeviceUpdated={() => {
                if (typeof onRefreshTvs === 'function') onRefreshTvs();
              }}
              knownLocations={knownLocations}
              hardwareChangeBlastRadius={
                selectedTV?.deviceId
                  ? blastRadiusByDeviceId[selectedTV.deviceId]
                  : null
              }
            />
          </div>
        )}
      </div>

      {deleteTarget && (
        <DeleteScreenModal
          device={deleteTarget}
          deleting={deleting}
          error={deleteError}
          onCancel={() => { if (!deleting) { setDeleteTarget(null); setDeleteError(null); } }}
          onConfirm={async () => {
            setDeleting(true);
            setDeleteError(null);
            try {
              const res = await deleteScreen(deleteTarget.deviceId);
              setDeleteTarget(null);
              if (typeof onRefreshTvs === 'function') onRefreshTvs();
              // eslint-disable-next-line no-console
              console.log('[screens] deleted', deleteTarget.deviceId, res?.cascaded);
            } catch (err) {
              setDeleteError(err?.message || 'Failed to delete screen');
            } finally {
              setDeleting(false);
            }
          }}
        />
      )}
    </div>
  );
};

/* ── Delete-screen confirm modal ───────────────────────────────────── */
const DeleteScreenModal = ({ device, deleting, error, onCancel, onConfirm }) => {
  const name = getDeviceDisplayName(device);
  return (
    <div
      className="screens-delete-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-screen-title"
      onClick={onCancel}
    >
      <div
        className="screens-delete-modal__box"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="screens-delete-modal__icon">
          <Trash2 size={20} />
        </div>
        <h3 id="delete-screen-title" className="screens-delete-modal__title">
          Delete screen "{name}"?
        </h3>
        <p className="screens-delete-modal__body">
          This will permanently remove the screen and cascade:
        </p>
        <ul className="screens-delete-modal__list">
          <li>All schedules targeting this screen</li>
          <li>Any timeline slots + loop configurations for it</li>
          <li>Its TV assignment (the tvId slot frees up)</li>
        </ul>
        <p className="screens-delete-modal__body screens-delete-modal__body--dim">
          Play history and audit events are kept. If the TV reconnects
          it will come back as a fresh, unconfigured screen.
        </p>
        {error && (
          <div className="screens-delete-modal__error" role="alert">{error}</div>
        )}
        <div className="screens-delete-modal__actions">
          <Button variant="ghost" onClick={onCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete screen'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ── Small inline helpers ──────────────────────────────────────────── */

const StatTile = ({ tone = 'neutral', icon, label, value, caption, active, onClick }) => (
  <div
    className={[
      'screens-stat',
      `screens-stat--${tone}`,
      active && 'screens-stat--active',
    ].filter(Boolean).join(' ')}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    aria-pressed={active}
  >
    <div className="screens-stat__top">
      <p className="screens-stat__label">{label}</p>
      <span className="screens-stat__icon">{icon}</span>
    </div>
    <p className="screens-stat__value">{value}</p>
    {caption && <p className="screens-stat__caption">{caption}</p>}
  </div>
);


const LoadingRows = ({ count = 5 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="screen-row screen-row--skeleton">
        <Skeleton variant="rect" width="48px" height="32px" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <Skeleton variant="text" width="60%" style={{ height: 14 }} />
          <Skeleton variant="text" width="40%" style={{ height: 11 }} />
        </div>
        <Skeleton variant="text" width="75%" style={{ height: 12 }} />
        <Skeleton variant="text" width="55%" style={{ height: 12 }} />
        <Skeleton variant="rect" width="64px" height="20px" style={{ borderRadius: 999 }} />
        <div />
      </div>
    ))}
  </>
);

export default ScreensView;
